// PipecatVoiceBotWithAvatar.jsx
import React from "react";
import {
  PipecatClientProvider,
  PipecatClientAudio,
  usePipecatClient,
  useRTVIClientEvent,
} from "@pipecat-ai/client-react";
import { PipecatClient, RTVIEvent } from "@pipecat-ai/client-js";
import { SmallWebRTCTransport } from "@pipecat-ai/small-webrtc-transport";

// 🧿 Your avatar bits
import SteffiAvatarPlayer from "./SteffiAvatarPlayer";
import { TALK_LOOP, SLEEP_LOOP, NEUTRAL_BLINK, MUSIC } from "./sequences";

// ----- Small helper hook: state machine for the avatar -----
function useAvatarStateMachine({ idleSeconds = 20 } = {}) {
  const [botSpeaking, setBotSpeaking] = React.useState(false);
  const [userSpeaking, setUserSpeaking] = React.useState(false);
  const [sessionStatus, setSessionStatus] = React.useState("disconnected"); // connected|disconnected
  const [music, setMusic] = React.useState(false);
  const [lastActivityAt, setLastActivityAt] = React.useState(() => Date.now());

  // Update "activity" whenever speech or music toggles
  const bumpActivity = React.useCallback(() => setLastActivityAt(Date.now()), []);
  React.useEffect(() => { bumpActivity(); }, [botSpeaking, userSpeaking, music, sessionStatus, bumpActivity]);

  // Idle -> sleep
  const [sleeping, setSleeping] = React.useState(false);
  React.useEffect(() => {
    const t = setInterval(() => {
      const idleFor = (Date.now() - lastActivityAt) / 1000;
      setSleeping(idleFor >= idleSeconds && sessionStatus === "connected" && !botSpeaking && !userSpeaking && !music);
    }, 500);
    return () => clearInterval(t);
  }, [lastActivityAt, idleSeconds, sessionStatus, botSpeaking, userSpeaking, music]);

  // Pick a sequence by priority
  const sequence = React.useMemo(() => {
    if (botSpeaking) return TALK_LOOP;       // 1) bot talking
    if (music) return MUSIC;                 // 2) music playing mode
    if (sleeping || sessionStatus !== "connected") return SLEEP_LOOP; // 3) sleep if idle or disconnected
    // (optionally show a subtle listening animation when user speaks; here we keep neutral)
    if (userSpeaking) return NEUTRAL_BLINK;
    return NEUTRAL_BLINK;                    // default idle
  }, [botSpeaking, userSpeaking, music, sleeping, sessionStatus]);

  return {
    botSpeaking, setBotSpeaking,
    userSpeaking, setUserSpeaking,
    sessionStatus, setSessionStatus,
    music, setMusic,
    sequence,
  };
}

// ----- Component that wires Pipecat events into the state machine -----
function AvatarController() {
  const {
    setBotSpeaking,
    setUserSpeaking,
    setSessionStatus,
    setMusic,
    sequence,
  } = useAvatarStateMachine({ idleSeconds: 20 });

  // 1) Standardized speaking/listening events (if your build emits them)
  useRTVIClientEvent(RTVIEvent.BotStartedSpeaking, React.useCallback(() => setBotSpeaking(true), [setBotSpeaking]));
  useRTVIClientEvent(RTVIEvent.BotStoppedSpeaking, React.useCallback(() => setBotSpeaking(false), [setBotSpeaking]));
  useRTVIClientEvent(RTVIEvent.UserStartedSpeaking, React.useCallback(() => setUserSpeaking(true), [setUserSpeaking]));
  useRTVIClientEvent(RTVIEvent.UserStoppedSpeaking, React.useCallback(() => setUserSpeaking(false), [setUserSpeaking]));

  // 2) Your custom server messages from backend (we used rtvi.send_server_message)
  useRTVIClientEvent(
    RTVIEvent.ServerMessage,
    React.useCallback((msg) => {
      if (!msg || msg.label !== "state") return;
      switch (msg.type) {
        case "session":
          setSessionStatus(msg.data?.status || "connected"); // "connected" | "disconnected"
          break;
        case "music_on":
          setMusic(true);
          break;
        case "music_off":
          setMusic(false);
          break;
        // Optional: if you emit these from backend instead of relying on standardized events
        case "bot_started_speaking":
          setBotSpeaking(true);
          break;
        case "bot_stopped_speaking":
          setBotSpeaking(false);
          break;
        case "user_started_speaking":
          setUserSpeaking(true);
          break;
        case "user_stopped_speaking":
          setUserSpeaking(false);
          break;
        default:
          // ignore others
          break;
      }
    }, [setSessionStatus, setMusic, setBotSpeaking, setUserSpeaking])
  );

  return (
    <div className="flex flex-col items-center gap-4">
      <SteffiAvatarPlayer sequence={sequence} className="w-48" />
      {/* Debug status (optional) */}
      {/* <div className="text-xs opacity-60">sequence: {sequence?.name || "unknown"}</div> */}
    </div>
  );
}

// ----- Voice controls + audio playback -----
function VoiceControls() {
  const client = usePipecatClient();

  const start = async () => {
    // (Optional) unlock audio on click for autoplay policies
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        if (ctx.state === "suspended") await ctx.resume();
      }
    } catch {}
    await client.connect({ webrtcUrl: "http://localhost:7860/api/offer" });
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <button onClick={start} className="px-3 py-2 rounded bg-black text-white">Connect</button>
      <PipecatClientAudio />
      <AvatarController />
    </div>
  );
}

// ----- Top-level provider -----
export default function PipecatVoiceBotWithAvatar() {
  const client = React.useMemo(() => {
    return new PipecatClient({
      transport: new SmallWebRTCTransport({
        enableMic: true,
        enableSpeaker: true,
      }),
      enableMic: true,
      enableCam: false,
    });
  }, []);

  return (
    <PipecatClientProvider client={client}>
      <VoiceControls />
    </PipecatClientProvider>
  );
}